import React from 'react';
import { Filter, FilterType } from '../../../models/filter';
import useQueryParams from '../../../hooks/useQueryParams';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel
} from '@headlessui/react';
import { MinusSmIcon, PlusSmIcon } from '@heroicons/react/solid';
import { classNames } from '../../../helpers/classes';

const GenericFilter: React.FC<{
  data: Filter;
  mobile?: boolean;
  disabled: boolean;
  ui?: string;
}> = ({ data, mobile, disabled, ui }) => {
  const [query, setQuery] = useQueryParams();
  const { code, options, name } = data || {};
  const value = query[code] || [];

  const onChange = (
    { target }: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const { checked } = target;

    if (checked) {
      setQuery({
        [code]: typeof value === 'string' ? [value, id] : [...value, id]
      });
    } else {
      setQuery({
        [code]:
          typeof value === 'string'
            ? undefined
            : [...value].filter(c => c !== id)
      });
    }
  };

  const isChecked = (id: string): boolean => {
    if (typeof value === 'string') {
      return value === id;
    }
    return value.includes(id);
  };

  return (
    <Disclosure
      as="div"
      className={`border-t px-4 py-6 ${
        ui ? 'border-white border-opacity-40' : 'border-black border-opacity-20'
      }`}
    >
      {({ open }) => (
        <>
          <h3 className="-mx-2 -my-3 flow-root">
            <DisclosureButton
              className={`${mobile ? 'bg-white' : ''} ${
                !mobile && ui ? 'text-white' : 'text-black'
              } px-2 py-3 w-full flex items-center justify-between opacity-60 hover:opacity-100 text-left`}
            >
              <span className="font-bold capitalize">{name}</span>
              <span className="ml-6 flex items-center">
                {open ? (
                  <MinusSmIcon className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <PlusSmIcon className="h-5 w-5" aria-hidden="true" />
                )}
              </span>
            </DisclosureButton>
          </h3>
          <DisclosurePanel className="mt-6 max-h-56 overflow-y-auto">
            <div className="space-y-2">
              {options.map((option, optionIdx) => {
                const optionName = `filter-mobile-${code}-${optionIdx}`;
                const checked = isChecked(String(option.id));

                if (code === FilterType.Color) {
                  return (
                    <div key={option.id} className="flex items-center">
                      <input
                        disabled={disabled}
                        id={optionName}
                        name={optionName}
                        defaultValue={value}
                        type="checkbox"
                        checked={isChecked(String(option.id))}
                        onChange={ev => onChange(ev, String(option.id))}
                        className="hidden h-4 w-4 border-gray-300 rounded text-primary focus:ring-primary disabled:opacity-50"
                      />
                      <label
                        htmlFor={optionName}
                        className={classNames(
                          checked ? 'text-primary font-bold' : 'opacity-60',
                          !mobile && ui ? 'text-white' : '',
                          ' ml-1 min-w-0 flex-1 cursor-pointer flex items-center'
                        )}
                      >
                        {option.name}
                      </label>
                    </div>
                  );
                }

                return (
                  <div key={option.id} className="flex items-center">
                    <input
                      disabled={disabled}
                      id={optionName}
                      name={optionName}
                      defaultValue={value}
                      type="checkbox"
                      checked={checked}
                      onChange={ev => onChange(ev, String(option.id))}
                      className="h-4 w-4 border-gray-300 rounded text-primary focus:ring-primary ml-1 disabled:opacity-50"
                    />
                    <label
                      htmlFor={optionName}
                      className={classNames(
                        !mobile && ui ? 'text-white' : '',
                        'ml-3 min-w-0 flex-1 text-black opacity-60'
                      )}
                    >
                      {option.name}
                    </label>
                  </div>
                );
              })}
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
};

export default GenericFilter;
